import Box from '@mui/material/Box';
import ChartTooltip from 'client/modules/Analyze/components/ChartTooltip';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

const W = 1180;
const H = 360;
const PAD_L = 46;
const PAD_R = 28;
const PAD_T = 30;
const PAD_B = 42;
const PW = W - PAD_L - PAD_R;
const PH = H - PAD_T - PAD_B;

// One score line per series over the period buckets, with a hover tooltip
// listing each series' score and answer volume at the hovered period.
function OverTimeComparisonChart({ buckets, series, formatNumber }) {
  const [active, setActive] = useState(null);
  const n = buckets.length;
  if (!n || !series.length) {
    return null;
  }
  const slot = n > 1 ? PW / (n - 1) : 0;
  const xAt = i => (n > 1 ? PAD_L + slot * i : PAD_L + PW / 2);

  const all = [];
  series.forEach(s =>
    buckets.forEach(b => {
      const cell = b.values[s.key];
      if (cell != null) {
        all.push(cell.score);
      }
    }),
  );
  if (!all.length) {
    all.push(0);
  }
  let lo = Math.min(...all, 0);
  let hi = Math.max(...all);
  if (hi === lo) {
    hi = lo + 1;
  }
  const padv = Math.max(4, Math.round((hi - lo) * 0.15));
  lo -= padv;
  hi += padv;
  const spanV = hi - lo || 1;
  const yAt = v => PAD_T + PH * (1 - (v - lo) / spanV);
  const ticks = [0, 1, 2, 3, 4].map(t => lo + (hi - lo) * (t / 4));

  const activeBucket = active != null ? buckets[active] : null;

  return (
    <Box sx={{ position: 'relative' }} onMouseLeave={() => setActive(null)}>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        sx={{ width: '100%', height: 'auto', display: 'block' }}
      >
        {ticks.map((v, t) => {
          const y = yAt(v);
          return (
            <g key={`grid-${t}`}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#EEF1F5" strokeWidth={1} />
              <text
                x={PAD_L - 12}
                y={y + 4}
                textAnchor="end"
                fontSize={12}
                fill="#A6AFBD"
                fontFamily="Figtree"
              >
                {formatNumber(Math.round(v))}
              </text>
            </g>
          );
        })}

        {lo < 0 && hi > 0 && (
          <line
            x1={PAD_L}
            y1={yAt(0)}
            x2={W - PAD_R}
            y2={yAt(0)}
            stroke="#C7CFDA"
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
        )}

        {active != null && (
          <line
            x1={xAt(active)}
            y1={PAD_T}
            x2={xAt(active)}
            y2={PAD_T + PH}
            stroke="#D6DEE8"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        )}

        {buckets.map((b, i) => (
          <text
            key={`x-${b.label}-${i}`}
            x={xAt(i)}
            y={H - 12}
            textAnchor="middle"
            fontSize={13}
            fill="#5B6675"
            fontWeight={600}
            fontFamily="Figtree"
          >
            {b.label}
          </text>
        ))}

        {series.map(s => {
          const pts = buckets
            .map((b, i) => ({
              x: xAt(i),
              y: b.values[s.key] != null ? yAt(b.values[s.key].score) : null,
            }))
            .filter(p => p.y != null);
          if (!pts.length) {
            return null;
          }
          const path = `M${pts.map(p => `${p.x},${p.y}`).join(' L')}`;
          return (
            <g key={s.key}>
              <path
                d={path}
                fill="none"
                stroke={s.color}
                strokeWidth={2.6}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {buckets.map((b, i) =>
                b.values[s.key] != null ? (
                  <circle
                    key={i}
                    cx={xAt(i)}
                    cy={yAt(b.values[s.key].score)}
                    r={active === i ? 5 : 3.5}
                    fill="#fff"
                    stroke={s.color}
                    strokeWidth={2}
                  />
                ) : null,
              )}
            </g>
          );
        })}

        {/* hover hit areas */}
        {buckets.map((b, i) => (
          <rect
            key={`hit-${i}`}
            x={i === 0 ? PAD_L : xAt(i) - slot / 2}
            y={PAD_T}
            width={i === 0 || i === n - 1 ? slot / 2 + (n > 1 ? 0 : PW) : slot}
            height={PH}
            fill="transparent"
            onMouseEnter={() => setActive(i)}
          />
        ))}
      </Box>

      {activeBucket && (
        <ChartTooltip
          xPct={(xAt(active) / W) * 100}
          title={activeBucket.label}
          rows={series.map(s => {
            const cell = activeBucket.values[s.key];
            return {
              label: s.label,
              color: s.color,
              value: cell != null ? formatNumber(cell.score) : '—',
              sub: cell != null ? `${formatNumber(cell.total)} resp.` : undefined,
            };
          })}
        />
      )}
    </Box>
  );
}

OverTimeComparisonChart.propTypes = {
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      values: PropTypes.object.isRequired,
    }),
  ).isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  formatNumber: PropTypes.func.isRequired,
};

export default OverTimeComparisonChart;
